// AddField.tsx
import { useState } from 'react';
import Dropdown from '../../../../../components/Dropdown/Dropdown';
import CustomTextInput from '../../InputFields/CustomTextInput';
import CustomToggle from '../../InputFields/CustomToggel';
import Button from '../../../../../components/Button/Button';
import { useToast } from '../../../../../components/Toast/ToastContext';
import {
  usePostTabsFieldMutation,
  usePostTabsFieldsMutation,
} from '../../../../../services/fieldApi/FieldApi';
import { useParams } from 'react-router-dom';
import { ISectionItem } from '../../../../../services/plaintiffSheets/plaintiffSheetsTypes';
import InformationDataType from '../FieldInput/InformationDataType';
import NumberInput from '../../InputFields/NumberInput';
import TableDataType from '../FieldInput/TableDataType';
import Icons from '../../../../../assets/index';

const FieldOptions = [
  { value: 'information', label: 'Information' },
  { value: 'table', label: 'Table' },
];

interface IAddSectionProps {
  onClose: () => void;
  tabsData?: ISectionItem;
  factSheetId?: string;
  tabsId?: string;
  sectionId?: string;
  onFieldAdded?: any;
}

type Column = {
  label: string;
  dataType: 'Text Input' | 'Number' | 'Date' | 'Dropdown';
};

type TableData = {
  columns: Column[];
  rows: string[];
  groupConfig: {
    groupName: string;
    selectedColumns: number[];
  };
  tabName: string;
};

const AddTab: React.FC<IAddSectionProps> = ({
  onClose,
  tabsData,
  tabsId,
  sectionId,
  onFieldAdded,
}) => {
  const { addToast } = useToast();
  const { id } = useParams();
  const isEditMode = false;
  const [AddTabs, { isLoading: addLoading }] = usePostTabsFieldMutation();
  const [AddTabsField, { isLoading: addFieldLoading }] = usePostTabsFieldsMutation();
  const [formData, setFormData] = useState<{
    name: string;
    placeholder: string;
    defaultValue: string;
    helpText: string;
    options: { name: string; action?: string }[];
    beforeLinkDescription: string;
    informationContainerContent: string;
    inlineTriggerText: string;
    modalContent: string;
    date: string;
  }>({
    name: '',
    placeholder: '',
    defaultValue: '',
    helpText: '',
    informationContainerContent: '',
    modalContent: '',
    beforeLinkDescription: '',
    inlineTriggerText: '',
    date: '',
    options: [{ name: '' }],
  });

  const [selected, setSelected] = useState('');
  const [error, setError] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [tableErrors, setTableErrors] = useState<{ [key: string]: string }>({});
  // Added for table-specific errors
  const [isMandatory, setIsMandatory] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const [columns, setColumns] = useState<number>(1);

  const [rows, setRows] = useState<number>(1);
  const [isRowsHeader, setIsRowsHeader] = useState(false);
  const [isColumnsHeader, setIsColumnsHeader] = useState(true);
  const [isColumnsGrouping, setIsColumnsGrouping] = useState(false);
  const [isExpandableTable, setIsExpandableTable] = useState(false);
  const [tableData, setTableData] = useState<TableData>({
    columns: [],
    rows: [],
    groupConfig: {
      groupName: '',
      selectedColumns: [],
    },
    tabName: '',
  });

  const [isPopulateData, setIsPopulateData] = useState(false);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    const updatedErrors = { ...errors, [name]: '' };

    setFormData(updatedFormData);
    setErrors(updatedErrors);
  };

  const handleChildDataChange = (updatedValues: Partial<any>) => {
    const updatedFormData = {
      ...formData,
      ...updatedValues,
    };

    const updatedErrors = { ...errors };

    // Clear option-related errors if options are now filled correctly
    if (updatedValues.options) {
      const allFilled = updatedValues.options.every((opt: any) => opt.name.trim() !== '');
      if (allFilled) {
        delete updatedErrors.dropdown;
        delete updatedErrors.emptyField;
      }
    }

    setFormData(updatedFormData);
    setErrors(updatedErrors);
  };

  const handleFieldTypeChange = (value: string) => {
    setSelected(value);
    setError(false);
    // Reset table errors when changing field type
    setTableErrors({});
  };

  const handleTableDataChange = (data: any) => {
    setTableData(data);
    // Clear table errors when data changes
    setTableErrors({});
  };

  const handleAddField = async () => {
    const newErrors: any = {};
    const newTableErrors: { [key: string]: string } = {};
    let data: any = {};

    if (!formData.modalContent.trim() && selected === 'information' && isModal) {
      newErrors.modalContent = 'Please provide modal content.';
    }
    if (!formData.beforeLinkDescription.trim() && selected === 'information' && isModal) {
      newErrors.beforeLinkDescription = 'Please provide relevant description.';
    }

    if (!formData.informationContainerContent.trim() && selected === 'information' && !isModal) {
      newErrors.informationContainerContent = 'Please provide information container content.';
    }

    if (
      formData?.options?.length === 1 &&
      formData?.options[0]?.name?.trim() === '' &&
      selected === 'dropdown'
    ) {
      newErrors.dropdown = 'Please mention at least one option to use this field effectively.';
    }

    if (!selected) {
      setError(true);
    } else {
      setError(false);
    }

    // Validate table fields if table is selected
    if (selected === 'table') {
      let hasTableErrors = false;

      // Validate row headers
      if (isRowsHeader) {
        tableData.rows.forEach((row, index) => {
          if (!row.trim()) {
            newTableErrors[`row_${index}`] = 'Please provide a relevant row header.';
            hasTableErrors = true;
          }
        });
      }

      // Validate column headers
      if (isColumnsHeader) {
        tableData.columns.forEach((col, index) => {
          if (!col.label.trim()) {
            newTableErrors[`column_${index}`] = 'Please provide a column name.';
            hasTableErrors = true;
          }
          if (!col.dataType) {
            newTableErrors[`columnType_${index}`] =
              'Column type cannot be empty. Please select an option.';
            hasTableErrors = true;
          }
        });
      }

      // Validate group configuration if enabled
      if (isColumnsGrouping) {
        if (!tableData.groupConfig.groupName.trim()) {
          newTableErrors.groupName = 'Please provide a group name.';
          hasTableErrors = true;
        }

        if (tableData.groupConfig.selectedColumns?.length === 0) {
          newTableErrors.selectedColumns = 'Please select at least one column.';
          hasTableErrors = true;
        }
      }

      // Update the table errors state
      setTableErrors(newTableErrors);

      // If there are table errors, add a general error message
      if (hasTableErrors) {
        newErrors.table = 'Please fix all table field errors.';
      }
    }

    // Check if there are any errors
    if (Object.keys(newErrors).length > 0 || Object.keys(newTableErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // Prepare data based on selected field type

    if (selected === 'information' && !isModal) {
      data = {
        label: tabsData?.name,
        fieldType: selected,
        required: false,
        config: {
          modalContent: formData.informationContainerContent,
        },
      };
    }

    if (selected === 'information' && isModal) {
      data = {
        label: tabsData?.name,
        fieldType: selected,
        required: false,
        config: {
          beforeLinkDescription: formData.beforeLinkDescription,
          inlineTriggerText: formData.inlineTriggerText,
          modalContent: formData.modalContent,
        },
      };
    }

    if (selected === 'table') {
      data = {
        label: tabsData?.name,
        required: isMandatory,
        tabName: tableData?.tabName,
        ispopulated: isPopulateData,
        columnHeaders:
          tableData.columns.some((col) => !col.label || !col.dataType) || !isColumnsHeader
            ? []
            : tableData.columns,
        rowHeaders: tableData.rows.some((row) => row === '') || !isRowsHeader ? [] : tableData.rows,
        columnGroups: isColumnsGrouping
          ? [
              {
                name: tableData?.groupConfig?.groupName,
                columns: isColumnsGrouping
                  ? tableData.groupConfig.selectedColumns
                      .map((index) => tableData.columns[index]?.label)
                      .filter((label) => !!label)
                  : [],
              },
            ]
          : [],
        config: {
          dimensions: {
            columns: tableData.columns.some((col) => !col.label || !col.dataType)
              ? 1
              : tableData?.columns?.length,
            rows: rows,
          },
          headers: {
            rowHeader: isRowsHeader,
            columnHeader: isColumnsHeader,
          },
          columnGroupingEnabled: isColumnsGrouping,
          additionalRows: isExpandableTable,
        },
      };
    }

    try {
      let response;

      if (selected === 'table') {
        response = await AddTabs({
          factSheetId: id,
          sectionId: sectionId,
          tabFieldId: tabsId,
          tabId: tabsData?.tab_id,
          data: data,
        }).unwrap();

        onFieldAdded();
      } else {
        response = await AddTabsField({
          factSheetId: id,
          sectionId: sectionId,
          tabFieldId: tabsId,
          tabId: tabsData?.tab_id,
          data: data,
        }).unwrap();
      }

      if (response.status === 'success') {
        addToast(
          isEditMode ? 'Field Updated' : 'Field Added',
          isEditMode ? 'Field updated successfully.' : 'Field added successfully.',
          'success'
        );
        onClose();
      }
    } catch (error) {
      addToast(
        isEditMode ? 'Update Failed' : 'Addition Failed',
        isEditMode
          ? 'Failed to update field. Please try again.'
          : 'Failed to add field. Please try again.',
        'error'
      );
    }
  };

  const handleClearAll = () => {
    setFormData({
      name: '',
      helpText: '',
      defaultValue: '',
      placeholder: '',
      options: [],
      informationContainerContent: '',
      beforeLinkDescription: '',
      inlineTriggerText: '',
      modalContent: '',
      date: '',
    });
    setSelected('');
    setIsMandatory(false);
    setErrors({});
    setTableErrors({});
    setError(false);
    setIsExpandableTable(false);
    setIsColumnsHeader(true);
    setIsColumnsGrouping(false);
    setIsRowsHeader(false);
    setIsModal(false);
    setTableData({
      columns: [],
      rows: [],
      groupConfig: {
        groupName: '',
        selectedColumns: [],
      },
      tabName: '',
    });
  };

  return (
    <div className="flex flex-col  h-full">
      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left side - now 55% width */}
        <div className="w-[49%] p-1 overflow-y-auto">
          <div className="mb-2">
            <label className="text-xs text-ds-gray-500 font-semibold">
              Field Type <span className="text-ds-red-400">*</span>
            </label>
            <p className="mt-1 text-xs text-ds-gray-300 mb-3">
              You can specify the type of data you expect people to enter; text, number, date,
              checkbox etc.
            </p>

            <Dropdown
              options={FieldOptions}
              selectedValue={selected}
              onChange={handleFieldTypeChange}
              error={error}
              errorMessage="Field type cannot be empty. Please select an option from the dropdown list."
            />
          </div>

          {(selected === 'information' || selected === 'table') && (
            <>
              <div className=" w-full">
                <div className="flex items-center gap-2 mb-4 bg-ds-mc-blue-100 px-3 py-4 rounded-md  ">
                  <span>
                    <Icons.infoIcon />
                  </span>
                  <p className="text-xs  text-ds-orange-900">
                    Field Name works as the title of the modal and does not appear for information,
                    table and tabs.
                  </p>
                </div>
              </div>
              {selected === 'information' && (
                <div className="mb-4">
                  <CustomToggle
                    label="Modal View"
                    description="Allow this information type to display a trigger text that opens a modal. Useful for detailed inputs or supporting instructions."
                    checked={isModal}
                    onChange={setIsModal}
                    className="pr-3"
                  />
                </div>
              )}
              {isModal && (
                <>
                  <div>
                    <CustomTextInput
                      label="Description Before Link"
                      name="beforeLinkDescription"
                      value={formData.beforeLinkDescription}
                      onChange={handleInputChange}
                      placeholder="Please provide relevant description"
                      required
                      description="You can specify a short message before the hyperlink to provide context."
                      error={errors.beforeLinkDescription}
                      className="text-xs text-ds-gray-500 font-semibold"
                    />
                  </div>
                  <div>
                    <CustomTextInput
                      label="Inline Trigger Text"
                      name="inlineTriggerText"
                      value={formData.inlineTriggerText}
                      onChange={handleInputChange}
                      placeholder="Please provide relevant trigger text"
                      description="You can specify the trigger text that appears as the hyperlink which opens the modal."
                      className="text-xs text-ds-gray-500 font-semibold"
                    />
                  </div>
                </>
              )}
            </>
          )}

          {selected === 'table' && (
            <>
              <div>
                <NumberInput
                  label="Number of Columns"
                  description="Number of columns within the table."
                  value={columns}
                  onChange={setColumns}
                  min={1}
                  max={100}
                  required
                />
              </div>
              <div className="mt-2">
                <NumberInput
                  label="Number of Rows"
                  description="Number of rows within the table."
                  value={rows}
                  onChange={setRows}
                  min={1}
                  max={100}
                  required
                />
              </div>

              <div className="mb-4 mt-4">
                <CustomToggle
                  label="Row Header"
                  description="Enable this is if the first columns within in the column is row header."
                  checked={isRowsHeader}
                  onChange={setIsRowsHeader}
                  className="pr-3"
                />
              </div>
              <div className="mb-4">
                <CustomToggle
                  label="Column Header"
                  description="Enable this is if the first row within table is column header."
                  checked={isColumnsHeader}
                  onChange={setIsColumnsHeader}
                  className="pr-3"
                />
              </div>
              <div className="mb-4">
                <CustomToggle
                  label="Column Grouping"
                  description="Enable grouping of related columns under a common header, allowing multi-level categorization."
                  checked={isColumnsGrouping}
                  onChange={setIsColumnsGrouping}
                  className="pr-3"
                />
              </div>
              <div className="mb-4">
                <CustomToggle
                  label="Expandable Table"
                  description="Enable if the user will have access to add more rows to the table."
                  checked={isExpandableTable}
                  onChange={setIsExpandableTable}
                  className="pr-3"
                />
              </div>

              <div className="mb-4">
                <CustomToggle
                  label="Data Auto-population"
                  description="Allows user to auto-populate data to the specific field with the help of identifier."
                  checked={isPopulateData}
                  onChange={setIsPopulateData}
                  className="pr-3"
                />
              </div>
            </>
          )}
        </div>

        {/* Thin border/divider */}
        <div className="border-l border-ds-gray-100"></div>

        {selected !== '' && (
          <>
            <div className="w-[51%] p-1 overflow-y-auto pt-0">
              {selected === 'information' && (
                <InformationDataType
                  onDataChange={handleChildDataChange}
                  isModal={isModal}
                  errors={errors}
                  setErrors={setErrors}
                />
              )}

              {selected === 'table' && (
                <TableDataType
                  numColumns={columns}
                  numRows={rows}
                  showRowConfig={isRowsHeader}
                  showColumnConfig={isColumnsHeader}
                  showGroupConfig={isColumnsGrouping}
                  onChange={handleTableDataChange}
                  initialData={tableData}
                  errors={tableErrors}
                  isPopulateData={isPopulateData}
                />
              )}
            </div>
          </>
        )}

        {selected === '' && (
          <h3 className="text-ds-cool-gray-400 flex  items-center m-auto ">
            Please select input type
          </h3>
        )}
      </div>

      {/* Bottom buttons */}
      <div className="flex justify-between  py-4 mb-4  gap-3">
        <div>
          <Button onClick={handleClearAll}>Clear All</Button>
        </div>
        <div className="flex gap-2 mr-4 ">
          <Button onClick={() => onClose()}>Cancel</Button>
          <Button color="primary" onClick={handleAddField} loading={addLoading || addFieldLoading}>
            Add
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddTab;
